import React, { useCallback, useEffect, useRef, useState } from "react";
import "../css/DeeplinkLoginOTPView.css";
import letsDoItIcon from "../../assets/images/lets-do-it-icon.svg";
import hideEye from "../../assets/images/otp-hide-eye.svg";
import showEye from "../../assets/images/otp-show-eye.svg";
import { CircularProgress } from "@mui/material";

interface DeeplinkLoginOTPViewProps {
  userEmail: string;
  length?: number;
  onChange: (otp: string) => void;
  onClickVerify: () => void;
  onClickChangeEmail: () => void;
  isLoading: boolean;
  resendTimer: number;
  onClickResendOTP: () => void;
}

const DeeplinkLoginOTPView: React.FC<DeeplinkLoginOTPViewProps> = ({
  userEmail,
  length = 6,
  onChange,
  onClickVerify,
  onClickChangeEmail,
  isLoading,
  resendTimer,
  onClickResendOTP,
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [isVisible, setIsVisible] = useState(false);

  const scrollToInput = useCallback((index: number) => {
    const input = inputRefs.current[index];
    input?.scrollIntoView({
      behavior: "smooth",
      block: "center", // Keeps input above keyboard
    });
  }, []);

  useEffect(() => {
    const handleResize = () => {
      // Find currently focused OTP input
      const activeInput = inputRefs.current.find(
        (input) => input === document.activeElement,
      );
      if (activeInput) {
        setTimeout(() => {
          activeInput.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 150);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isOtpComplete = (): boolean => {
    return inputRefs.current.every(
      (input) => input && input.value.length === 1,
    );
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    i: number,
  ): void => {
    const value = e.target.value.slice(-1);
    e.target.value = value;
    scrollToInput(i);
    if (value && i < length - 1) {
      inputRefs.current[i + 1]?.focus();
    }
    triggerOtpChange();
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    i: number,
  ): void => {
    if (e.key === "Backspace" && !e.currentTarget.value && i > 0) {
      inputRefs.current[i - 1]?.focus();
    }
  };

  const triggerOtpChange = (): void => {
    const otp = inputRefs.current.map((input) => input?.value || "").join("");
    onChange(otp);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const handleResendClick = () => {
    inputRefs.current.forEach((input) => {
      if (input) input.value = "";
    });
    onChange("");
    onClickResendOTP();
  };

  return (
    <div className="deeplink-login-otp-view-container">
      <div className="deeplink-login-otp-view-sub-container">
        <span>Email <span className="sub-text">Address</span></span>
        <div
          className="deeplink-login-otp-change-email"
          onClick={onClickChangeEmail}
        >
          Change Email
        </div>
      </div>
      <div className="deeplink-login-otp-view-email-field">{userEmail}</div>
      <div className="deeplink-login-otp-view-sub-container">
        <span>Email <span className="sub-text">OTP</span></span>
        <div onClick={() => setIsVisible(!isVisible)}>
          {isVisible ? (
            <img src={showEye} alt="show-eye" />
          ) : (
            <img src={hideEye} alt="hide-eye" />
          )}
        </div>
      </div>
      <div className="deeplink-login-otp-input-group">
        {Array.from({ length }).map((_, i) => (
          <input
            key={i}
            type={isVisible ? "tel" : "password"}
            inputMode="numeric"
            maxLength={1}
            className="otp-input"
            ref={(el) => {
              inputRefs.current[i] = el;
            }}
            onChange={(e) => handleChange(e, i)}
            onKeyDown={(e) => handleKeyDown(e, i)}
          />
        ))}
      </div>
      <div className="deeplink-login-otp-resend">
        {resendTimer > 0 ? (
          <span>Resend OTP in {formatTime(resendTimer)}</span>
        ) : (
          <span onClick={handleResendClick}>Resend OTP</span>
        )}
      </div>
      <div
        className="deeplink-login-otp-content-button"
        onClick={isOtpComplete() ? onClickVerify : undefined}
        style={{
          opacity: isOtpComplete() ? 1 : 0.5,
        }}
      >
        {isLoading ? (
          <CircularProgress size={30} color="inherit" />
        ) : (
          <>
            Continue
            <img
              src={letsDoItIcon}
              className="signin-otp-view-arrow-icon"
              alt="logo"
            />
          </>
        )}
      </div>
      {/* <div
        className="deeplink-login-otp-view-button-container"
        onClick={isOtpComplete() ? onClickVerify : undefined}
        style={{
          opacity: isOtpComplete() ? 1 : 0.5,
        }}
      >
        {isLoading ? (
          <CircularProgress size={30} color="inherit" />
        ) : (
          <>
            VERIFY
            <img
              src={whiteArrow}
              className="deeplink-login-otp-view-arrow-icon"
              alt="logo"
            />
          </>
        )}
      </div> */}
    </div>
  );
};

export default DeeplinkLoginOTPView;
